/**
 * Helper centralisé pour construire les URLs médias.
 *
 * 3 cas :
 * 1. Asset nouveau pipeline (variants thumb/medium/large/original) → routes /api/media/v2/{id}/{variant} avec ?auth=TOKEN
 * 2. URL legacy /api/media/property_photos/... → préfixé par BACKEND_URL avec ?auth=TOKEN
 * 3. URL absolue (https://images.unsplash.com/...) → utilisée telle quelle
 *
 * Utilisation typique :
 *   <img
 *     src={getMediaUrl(photo, 'medium')}
 *     srcSet={getMediaSrcSet(photo)}
 *     sizes="(max-width: 768px) 100vw, 400px"
 *     loading="lazy"
 *   />
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function _getToken() {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

function _appendAuth(url, shareToken) {
  // Mode 1 : share_token (pour les pages publiques)
  if (shareToken) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}share_token=${encodeURIComponent(shareToken)}`;
  }
  // Mode 2 : auth JWT (pour les utilisateurs connectés)
  const token = _getToken();
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}auth=${encodeURIComponent(token)}`;
}

function _resolveStringUrl(url, shareToken = null, variant = null) {
  if (!url) return null;
  // URL absolue externe → telle quelle
  if (/^https?:\/\//.test(url)) return url;

  // 🔑 URL pipeline v2 (string) : extraire asset_id et appliquer la variante demandée
  // Format attendu : "/api/media/v2/{asset_id}/{variant}" (médias servis par notre backend)
  const m = url.match(/^\/api\/media\/v2\/([^/?#]+)\/([a-z]+)(.*)$/);
  if (m) {
    const assetId = m[1];
    const effectiveVariant = variant || m[2]; // priorité au variant explicitement demandé
    // Cas public : utiliser la route /api/public/media/{token}/{id}/{variant}
    if (shareToken) {
      return _prefixBackend(`/api/public/media/${encodeURIComponent(shareToken)}/${assetId}/${effectiveVariant}`);
    }
    // Cas auth : recomposer l'URL v2 avec le bon variant + ?auth=
    return _appendAuth(_prefixBackend(`/api/media/v2/${assetId}/${effectiveVariant}`));
  }

  // URL relative interne (legacy, etc.) → préfixer + auth (ou share_token pour legacy)
  return _appendAuth(_prefixBackend(url), shareToken);
}

/**
 * Renvoie l'URL d'affichage d'un media pour la taille demandée.
 * @param {object|string|null} media — objet photo `{url, asset_id, variants:{thumb:{url}, medium:{url}, ...}}` OU string URL directe
 * @param {string} variant — 'thumb' (300px) | 'medium' (1200px, défaut) | 'large' (1800px) | 'original' (2400px)
 * @param {string=} shareToken — si fourni, utilise share_token au lieu d'auth JWT (pages publiques)
 * @returns {string|null}
 */
export function getMediaUrl(media, variant = 'medium', shareToken = null) {
  if (!media) return null;
  // Si c'est juste une string
  if (typeof media === 'string') {
    return _resolveStringUrl(media, shareToken, variant);
  }
  // 🔑 CAS PUBLIC + PIPELINE v2 : on doit utiliser /api/public/media/{share_token}/{asset_id}/{variant}
  // (route dédiée anti-énumération, la route v2 normale ne supporte que ?auth=JWT)
  if (shareToken && media.asset_id) {
    return _prefixBackend(`/api/public/media/${encodeURIComponent(shareToken)}/${media.asset_id}/${variant}`);
  }
  // 🔑 CAS AUTH + asset_id présent : construire l'URL v2 avec le variant demandé (sans dépendre du bloc variants)
  if (media.asset_id) {
    return _appendAuth(_prefixBackend(`/api/media/v2/${media.asset_id}/${variant}`));
  }
  // Si l'objet a des variants explicites (nouveau pipeline avec métadonnées)
  if (media.variants && media.variants[variant]?.url) {
    return _appendAuth(_prefixBackend(media.variants[variant].url), shareToken);
  }
  // Sinon fallback sur media.url (avec réécriture de variant si c'est une URL v2)
  if (media.url) {
    return _resolveStringUrl(media.url, shareToken, variant);
  }
  return null;
}

function _prefixBackend(url) {
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Construit un srcSet WebP responsive pour les balises img.
 * 3 variantes : thumb 300w / medium 1200w / large 1800w.
 *
 * - Si l'objet a un bloc `variants` (avec métadonnées width) → utilise les widths réelles
 * - Sinon, si l'objet a un asset_id (ou URL v2) → construit le srcset à partir des conventions
 *   (largeur nominale : thumb 300, medium 1200, large 1800)
 * - Sinon → undefined (fallback sur src classique)
 *
 * @param {object} media
 * @param {string=} shareToken — pour les pages publiques
 */
export function getMediaSrcSet(media, shareToken = null) {
  if (!media || typeof media === 'string') return undefined;

  const NOMINAL_WIDTHS = { thumb: 300, medium: 1200, large: 1800 };
  const variantsList = ['thumb', 'medium', 'large'];

  // Cas 1 : variants metadata présents
  if (media.variants) {
    const parts = [];
    variantsList.forEach(v => {
      const w = media.variants[v]?.width || NOMINAL_WIDTHS[v];
      const url = getMediaUrl(media, v, shareToken);
      if (url && w) parts.push(`${url} ${w}w`);
    });
    return parts.length > 0 ? parts.join(', ') : undefined;
  }

  // Cas 2 : asset_id présent (ou URL v2 extractible) → construire le srcset à partir des nominal widths
  const hasV2 = media.asset_id || (typeof media.url === 'string' && /^\/api\/media\/v2\//.test(media.url));
  if (hasV2) {
    const parts = [];
    variantsList.forEach(v => {
      const url = getMediaUrl(media, v, shareToken);
      if (url) parts.push(`${url} ${NOMINAL_WIDTHS[v]}w`);
    });
    return parts.length > 0 ? parts.join(', ') : undefined;
  }

  return undefined;
}

/**
 * Extracteur générique d'une URL de photo depuis un objet property (compat seed + legacy + nouveau).
 * @returns {string|null}
 */
export function getPropertyMainPhotoUrl(property, variant = 'medium') {
  if (!property) return null;
  const medias = property.medias || {};
  // Priorité 1 : photos[0] avec variants (nouveau pipeline)
  if (medias.photos && medias.photos[0]) {
    const u = getMediaUrl(medias.photos[0], variant);
    if (u) return u;
  }
  // Priorité 2 : photo_principale (legacy string ou nouvelle URL)
  if (medias.photo_principale) {
    return _resolveStringUrl(medias.photo_principale);
  }
  // Priorité 3 : photos_urls CSV legacy
  if (medias.photos_urls) {
    const first = medias.photos_urls.split(',')[0].trim();
    if (first) return _resolveStringUrl(first);
  }
  // Priorité 4 : thumbnail_url (généré par /api/properties/list)
  if (property.thumbnail_url) {
    return _resolveStringUrl(property.thumbnail_url);
  }
  return null;
}

/**
 * Idem mais retourne le srcSet WebP responsive si dispo (sinon undefined → fallback simple <img src>).
 */
export function getPropertyMainPhotoSrcSet(property) {
  const medias = property?.medias || {};
  if (medias.photos && medias.photos[0]) {
    return getMediaSrcSet(medias.photos[0]);
  }
  return undefined;
}
